<template>
  <el-dialog
    custom-class="program-editor-dialog"
    :visible="value"
    :close-on-click-modal="false"
    :show-close="false"
    :destroy-on-close="true"
    :before-close="onBeforeClose"
    width="1280px"
    top="4vh"
    @update:visible="(v) => $emit('input', v)"
  >
    <div class="programs-editor">

      <!-- Topbar -->
      <div class="ed-topbar">
        <div class="ed-topbar-left">
          <button type="button" class="ed-back-btn" @click="closeRequest" aria-label="Close editor">
            <i class="el-icon-close"/>
          </button>
          <div class="ed-title-block">
            <div class="qd-eyebrow">{{ statusLabel }}</div>
            <div class="ed-program-title">{{ program.title || 'New program' }}</div>
          </div>
        </div>
        <div class="ed-topbar-right">
          <span v-if="readinessHint" class="ed-readiness-hint">{{ readinessHint }}</span>
          <el-button
            v-if="canInstallTemplateContent"
            size="small"
            :loading="templateInstallBusy"
            :disabled="saving"
            @click="installTemplateContent"
          >
            <i class="el-icon-collection"/> Create missing items from template
          </el-button>
          <el-button
            size="small"
            class="btn-6C30CC"
            :loading="saving"
            :disabled="saving"
            @click="onSave"
          >Save program</el-button>
        </div>
      </div>

      <div v-if="saveError" class="ed-save-error" role="alert">
        <i class="el-icon-warning-outline"/>
        <span>{{ saveError }}</span>
      </div>

      <!-- Body: two-column layout (main + right rail) -->
      <div class="ed-body">

        <div class="ed-main">

          <!-- Basics card -->
          <section class="ed-card">
            <div class="ed-card-head">
              <span class="qd-eyebrow">Basics</span>
            </div>
            <div class="ed-form">
              <div class="ed-field">
                <label class="qd-eyebrow" for="program-title">Title</label>
                <el-input
                  id="program-title"
                  :value="program.title"
                  @input="(v) => updateField('title', v)"
                  placeholder="e.g., Your first 30 days, fall cohort"
                />
              </div>
              <div class="ed-field">
                <label class="qd-eyebrow" for="program-description">Description</label>
                <el-input
                  id="program-description"
                  type="textarea"
                  :rows="2"
                  :value="program.description"
                  @input="(v) => updateField('description', v)"
                  placeholder="One sentence on what participants get out of this program."
                />
              </div>
            </div>
          </section>

          <!-- Sequence card -->
          <section class="ed-card">
            <div class="ed-card-head">
              <span class="qd-eyebrow">Sequence</span>
              <span class="ed-helper">{{ items.length }} item{{ items.length === 1 ? '' : 's' }}</span>
            </div>

            <div class="ed-items">
              <div
                v-for="(item, idx) in items"
                :key="item.id"
                class="ed-item"
                :class="{ 'is-incomplete': !item.libraryId }"
              >
                <div class="ed-item-head">
                  <div class="ed-item-day">
                    <label class="qd-eyebrow" :for="`program-item-day-${item.id}`">Day</label>
                    <el-input-number
                      :id="`program-item-day-${item.id}`"
                      :value="(Number(item.offsetDays) || 0) + 1"
                      :min="1"
                      :max="366"
                      :controls="false"
                      size="mini"
                      @input="(v) => updateItem(idx, 'offsetDays', Math.max(0, (Number(v) || 1) - 1))"
                    />
                  </div>
                  <span class="ed-item-pill" :style="{ background: typeMeta(item.type).tint, color: typeMeta(item.type).tone }">
                    <i :class="typeMeta(item.type).icon"/>
                    {{ typeMeta(item.type).label }}
                  </span>
                  <div class="ed-item-spacer"></div>
                  <div class="ed-item-reorder">
                    <button
                      type="button"
                      class="ed-item-icon-btn"
                      :disabled="idx === 0"
                      @click="moveItem(idx, -1)"
                      :aria-label="`Move ${previewTitle(item)} earlier`"
                    ><i class="el-icon-top"/></button>
                    <button
                      type="button"
                      class="ed-item-icon-btn"
                      :disabled="idx === items.length - 1"
                      @click="moveItem(idx, 1)"
                      :aria-label="`Move ${previewTitle(item)} later`"
                    ><i class="el-icon-bottom"/></button>
                    <button
                      type="button"
                      class="ed-item-icon-btn is-danger"
                      @click="removeItem(idx)"
                      :aria-label="`Remove ${previewTitle(item)}`"
                    ><i class="el-icon-delete"/></button>
                  </div>
                </div>

                <div class="ed-item-body">
                  <library-picker
                    :value="item.libraryId || ''"
                    @input="(id) => updateItem(idx, 'libraryId', id || null)"
                    :items="libraryFor(item.type)"
                    :library-label="typeMeta(item.type).libraryLabel"
                    :icon-class="typeMeta(item.type).icon"
                    :tone="typeMeta(item.type).tone"
                    :search-placeholder="typeMeta(item.type).searchPlaceholder"
                    :intent="item.participantTitle || typeMeta(item.type).defaultIntent"
                    :create-path="typeMeta(item.type).createPath"
                    :loading="item.type === 'reel' && reelsLoading"
                    :load-error="item.type === 'reel' ? reelsLoadError : ''"
                    @retry="item.type === 'reel' && refreshReels()"
                  />

                  <div v-if="item.libraryId" class="ed-item-override">
                    <label class="qd-eyebrow" :for="`program-item-title-${item.id}`">Title participants see (optional)</label>
                    <el-input
                      :id="`program-item-title-${item.id}`"
                      :value="item.participantTitle"
                      @input="(v) => updateItem(idx, 'participantTitle', v)"
                      :placeholder="placeholderFor(item)"
                      size="small"
                    />
                  </div>

                  <!-- Share-vs-clone toggle for topic/poll/session.
                       Custom questions and sparks are per-user already, so
                       this toggle doesn't apply there. -->
                  <div v-if="isCloneable(item.type)" class="ed-item-share">
                    <el-switch
                      :value="item.shareMode === 'shared'"
                      :aria-label="`Sharing mode for ${previewTitle(item)}`"
                      @change="(v) => updateItem(idx, 'shareMode', v ? 'shared' : 'per_batch')"
                      active-color="#6c30cc"
                    />
                    <div class="ed-item-share-copy">
                      <div class="ed-item-share-title">
                        {{ item.shareMode === 'shared' ? 'One shared copy for all runs' : 'Separate copy per run' }}
                      </div>
                      <div class="ed-item-share-sub">
                        {{ shareModeHint(item) }}
                      </div>
                    </div>
                  </div>

                  <reward-picker
                    :value="item.completionReward"
                    :item-type="item.type"
                    :default-reason="defaultRewardReason(item)"
                    @input="(v) => updateItem(idx, 'completionReward', v)"
                  />
                </div>
              </div>

              <!-- Continuation "next step" card. Lives inside .ed-items so
                   it visually follows the last item rather than appearing
                   as a separate strip below the sequence. -->
              <div class="ed-add-card" :class="{ 'is-first': items.length === 0 }">
                <div class="ed-add-card__copy">
                  <div class="ed-add-card__title">
                    {{ items.length === 0 ? 'Add your first item' : 'Add the next item' }}
                  </div>
                  <div class="ed-add-card__sub">
                    Pick a type — it lands on day {{ nextDayHint }} and you can pick the content next.
                  </div>
                </div>
                <div class="ed-add-card__btns">
                  <button
                    v-for="t in TYPE_ORDER"
                    :key="t"
                    type="button"
                    class="ed-add-btn"
                    :style="{ '--add-tint': typeMeta(t).tint, '--add-tone': typeMeta(t).tone }"
                    @click="addItem(t)"
                  >
                    <span class="ed-add-btn__icon">
                      <i :class="typeMeta(t).icon"/>
                    </span>
                    <span class="ed-add-btn__text">
                      <span class="ed-add-btn__label">{{ typeMeta(t).label }}</span>
                      <span class="ed-add-btn__blurb">{{ typeMeta(t).blurb }}</span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </section>

        </div>

        <!-- Right rail: live participant-view preview of the sequence.
             Mirrors the aq-preview-* pattern from add/edit question — a
             page-tinted column with an eyebrow label that signals "this
             is how the entity reads in another context." -->
        <aside class="ed-preview" aria-label="Participant view preview">
          <div class="ed-preview-head">
            <div class="aq-preview-eyebrow">Participant view</div>
            <div class="ed-preview-sub">{{ previewSummary }}</div>
          </div>

          <div v-if="items.length === 0" class="ed-preview-empty">
            Your sequence will appear here as you add items.
          </div>

          <div v-else class="ed-preview-timeline">
            <div
              v-for="(group, gIdx) in groupedByDay"
              :key="'g' + group.day"
              class="ed-preview-day"
              :class="{ 'is-last': gIdx === groupedByDay.length - 1 }"
            >
              <div class="ed-preview-day-rail">
                <div class="ed-preview-day-badge">{{ dayLabel(group.day) }}</div>
                <div class="ed-preview-day-line" aria-hidden="true"></div>
              </div>
              <div class="ed-preview-day-items">
                <div
                  v-for="it in group.items"
                  :key="'pv-' + it.id"
                  class="ed-preview-item"
                  :class="{ 'is-incomplete': !it.libraryId }"
                >
                  <span
                    class="ed-preview-item-icon"
                    :style="{ background: typeMeta(it.type).tint, color: typeMeta(it.type).tone }"
                  >
                    <i :class="typeMeta(it.type).icon"/>
                  </span>
                  <div class="ed-preview-item-body">
                    <div class="ed-preview-item-title" :class="{ 'is-muted': !it.libraryId }">
                      {{ previewTitle(it) }}
                    </div>
                    <div class="ed-preview-item-meta">
                      <span>{{ typeMeta(it.type).label }}</span>
                      <span
                        v-if="isCloneable(it.type) && it.shareMode === 'shared'"
                        class="ed-preview-shared-pill"
                      >Shared</span>
                      <span
                        v-if="!it.libraryId"
                        class="ed-preview-needs-pill"
                      >Needs content</span>
                      <span
                        v-if="hasValidReward(it)"
                        class="ed-preview-reward-pill"
                      >+{{ it.completionReward.amount }} {{ it.completionReward.currency }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>

      </div>

    </div>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { PROGRAM_ONLY_KEY } from '../../store/huddle-helpers'
import LibraryPicker from './library-picker.vue'
import RewardPicker from './reward-picker.vue'
import { dayLabel, dayNumber } from './_program-labels'
import { buildProgramReelLibrary, loadProgramReels } from './_reel-library'

const TYPE_ORDER = ['session', 'topic', 'poll', 'checklist', 'reel', 'custom_question', 'spark']

const TYPE_META = {
  session: {
    label: 'Session',
    blurb: 'Live or recorded training',
    libraryLabel: 'University',
    icon: 'el-icon-video-play',
    tone: '#6c30cc',
    tint: '#f2ecfb',
    searchPlaceholder: 'Search University sessions…',
    defaultIntent: 'Pick a Happily University session, live or recorded. Triggers once the participant has completed it (attended the live session or watched the video).',
    createPath: '/happily-village/university?compose=program',
  },
  topic: {
    label: 'Town Hall topic',
    blurb: 'An article to read',
    libraryLabel: 'Town Hall',
    icon: 'el-icon-document',
    tone: '#626262',
    tint: '#f6f7f9',
    searchPlaceholder: 'Search Town Hall topics…',
    defaultIntent: 'Pick a Town Hall topic. Triggers once the participant has read it.',
    createPath: '/happily-village/town-hall?compose=program&type=topic',
  },
  poll: {
    label: 'Poll',
    blurb: 'A quick vote',
    libraryLabel: 'Town Hall',
    icon: 'el-icon-data-line',
    tone: '#c89500',
    tint: '#fdf6dc',
    searchPlaceholder: 'Search Town Hall polls…',
    defaultIntent: 'Pick a Town Hall poll. Triggers once the participant has voted.',
    createPath: '/happily-village/town-hall?compose=program&type=poll',
  },
  checklist: {
    label: 'Checklist',
    blurb: 'Tasks to tick off',
    libraryLabel: 'Town Hall',
    icon: 'el-icon-finished',
    tone: '#6c30cc',
    tint: '#f2ecfb',
    searchPlaceholder: 'Search Town Hall checklists…',
    defaultIntent: 'Pick a Town Hall checklist. Triggers once the participant has ticked all required items.',
    createPath: '/happily-village/town-hall?compose=program&type=checklist',
  },
  reel: {
    label: 'Reel',
    blurb: 'A short video story',
    libraryLabel: 'Town Hall Reels',
    icon: 'el-icon-video-camera',
    tone: '#6c30cc',
    tint: '#f2ecfb',
    searchPlaceholder: 'Search published reels…',
    defaultIntent: 'Pick a published Town Hall Reel. It stays shared so participants see the final video and its reactions in one place.',
    createPath: '/happily-village/town-hall?content=reels',
  },
  custom_question: {
    label: 'Custom question',
    blurb: 'A question to answer',
    libraryLabel: 'Questions',
    icon: 'el-icon-chat-line-square',
    tone: '#1c8b9a',
    tint: '#e6f5f8',
    searchPlaceholder: 'Search Custom Questions…',
    defaultIntent: 'Pick a Custom Question authored in /questions. Triggers once the participant has answered it.',
    // compose=program opens the question composer with frequency
    // pre-set to Program-only, so questions made for a program land
    // in the right bucket.
    createPath: '/questions/custom?compose=program',
  },
  spark: {
    label: 'Spark set',
    blurb: 'Micro-learning cards',
    libraryLabel: 'Micro Learning',
    icon: 'el-icon-magic-stick',
    tone: '#c2185b',
    tint: '#fce4ec',
    searchPlaceholder: 'Search Micro Learning sets…',
    defaultIntent: 'Pick a Program-only Spark set from Micro Learning. Triggers once the participant has completed all its cards.',
    // compose=program opens the Micro Learning create-set dialog with
    // "Program only" pre-selected, so the set lands in the right bucket.
    createPath: '/sparks/micro-learning?compose=program',
  },
}

const PICK_PLACEHOLDER = {
  session: 'Pick a session',
  topic: 'Pick a topic',
  poll: 'Pick a poll',
  checklist: 'Pick a checklist',
  reel: 'Pick a reel',
  custom_question: 'Pick a question',
  spark: 'Pick a spark set',
}

// Per-type completion semantics — verb is what the participant *does*
// to fire the trigger, noun is the participant-facing label used in the
// fallback reason copy. Sessions cover both live (attended) and video
// (watched) under the umbrella verb "completing" so authors don't have
// to disambiguate at authoring time. Used by defaultRewardReason() so
// the seeded grant message uses the right action word per item type
// ("For reading X" vs "For voting in X" vs "For completing X").
const COMPLETION_VERB = {
  session:         { verb: 'completing',  noun: 'session' },
  topic:           { verb: 'reading',     noun: 'topic' },
  poll:            { verb: 'voting in',   noun: 'poll' },
  checklist:       { verb: 'completing',  noun: 'checklist' },
  reel:            { verb: 'watching',    noun: 'reel' },
  custom_question: { verb: 'answering',   noun: 'question' },
  spark:           { verb: 'completing',  noun: 'spark set' },
}

let _idSeq = 0
function newItemId() {
  _idSeq += 1
  return 'it_' + Date.now().toString(36) + '_' + _idSeq.toString(36)
}

function newProgramId() {
  return 'pg_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
}

// A new program carries its id from creation, not from the first save.
// With a stable id, every save is an idempotent `.doc(id).set()` on the
// same document — so a double-clicked Save, a re-opened editor, or any
// repeated save can't mint a second copy.
function blankProgram() {
  return {
    id: newProgramId(),
    title: '',
    description: '',
    items: [],
  }
}

export default {
  name: 'program-editor',
  components: { LibraryPicker, RewardPicker },
  props: {
    value: { type: Boolean, default: false },
    initialProgram: { type: Object, default: null },
    templateInstallBusy: { type: Boolean, default: false },
    saveHandler: { type: Function, required: true },
  },
  data() {
    return {
      TYPE_ORDER,
      program: blankProgram(),
      openProgramFingerprint: '',
      libraryRefreshAt: 0,
      libraryFocusHandler: null,
      reelsAll: [],
      reelsLoading: false,
      reelsLoadError: '',
      saving: false,
      saveError: '',
    }
  },
  computed: {
    // Real Vuex getters from the surrounding feature modules. The picker
    // shows real library content; refreshLibraries() (called on editor
    // open) keeps these fresh so content authored after the page loaded
    // — e.g. a new Program-only question — shows up without a reload.
    ...mapGetters('happilyvillageUniversity', { _sessionsAll: 'sessions_all' }),
    ...mapGetters('happilyvillageTownhall',  { _townhallAll: 'townhall_all' }),
    ...mapGetters('question',                { _questionsAll: 'questionsall' }),
    ...mapGetters('custom',                  { _customQuestionsAll: 'questioncustomAll' }),
    ...mapGetters('sparks',                  { _sparkSetsAll: 'sparkCustom' }),
    // Single pass over the townhall collection — partitioned by type and
    // pre-filtered for program clones. The four townhall-backed builders
    // (topic, poll, checklist) read from these slices instead of each
    // doing their own full filter.
    _townhallByType() {
      const out = { topic: [], poll: [], checklist: [] }
      ;(this._townhallAll || []).forEach((t) => {
        if (!t || !out[t.type] || this._isProgramClone(t)) return
        out[t.type].push(t)
      })
      return out
    },
    _sessionsLibrarySource() {
      return (this._sessionsAll || []).filter((s) => s && !this._isProgramClone(s))
    },
    items() {
      return this.program.items || []
    },
    statusLabel() {
      switch ((this.initialProgram || {}).status) {
        case 'ready':    return 'Ready to run'
        default:         return 'Draft'
      }
    },
    readinessHint() {
      if (this.items.length === 0) return 'Add at least one item.'
      const missing = this.items.filter((it) => !it.libraryId).length
      if (missing > 0) {
        return `${missing} item${missing === 1 ? '' : 's'} still need${missing === 1 ? 's' : ''} content.`
      }
      // Surface malformed rewards as a non-blocking warning. The save
      // still goes through — the RN runtime ignores invalid grants —
      // but the author probably didn't mean to ship one.
      const badRewards = this.malformedRewardCount
      if (badRewards > 0) {
        return `${badRewards} reward${badRewards === 1 ? '' : 's'} need${badRewards === 1 ? 's' : ''} a value.`
      }
      return 'Ready to start a run.'
    },
    // Bucketed by offsetDays. Map preserves insertion order and
    // Array.sort is stable since ES2019, so the editor's order within
    // a day is preserved end-to-end without an explicit sequence field.
    groupedByDay() {
      const buckets = new Map()
      this.items.forEach((it) => {
        const day = Number(it.offsetDays) || 0
        if (!buckets.has(day)) buckets.set(day, [])
        buckets.get(day).push(it)
      })
      return Array.from(buckets.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([day, items]) => ({ day, items }))
    },
    previewSummary() {
      if (this.items.length === 0) return 'Empty program'
      const last = Math.max(...this.items.map((it) => Number(it.offsetDays) || 0))
      const span = last === 0 ? dayLabel(0) : `${dayLabel(0)} → ${dayLabel(last)}`
      const count = `${this.items.length} item${this.items.length === 1 ? '' : 's'}`
      return `${span} · ${count}`
    },
    // Surfaced in the "Add the next item" card so authors know which day
    // their next click will land on. Mirrors addItem()'s offset rule
    // (`lastOffset + 1`), then +1 again for the 1-indexed display.
    nextDayHint() {
      if (!this.items.length) return dayNumber(0)
      const lastOffset = Math.max(...this.items.map((item) => Number(item.offsetDays) || 0))
      return dayNumber(lastOffset + 1)
    },
    // Count of items whose completionReward is configured but malformed
    // (missing currency or non-positive amount). Surfaced in the
    // readiness hint so authors don't accidentally ship a no-op reward.
    malformedRewardCount() {
      return this.items.filter((it) => {
        const r = it.completionReward
        if (!r) return false
        return !r.currency || !(Number(r.amount) > 0)
      }).length
    },
    canInstallTemplateContent() {
      return !!(
        this.initialProgram &&
        this.initialProgram.templateRef &&
        this.items.some((it) => !it.libraryId)
      )
    },
    // Real Vuex-backed library, indexed by item type. Computed so a
    // program with N items doesn't re-walk the same arrays N×3 times
    // per render (libraryFor is called for the picker, the override
    // placeholder, and the preview-rail title).
    librariesByType() {
      return {
        session: this._buildSessionLibrary(),
        topic: this._buildTopicLibrary(),
        poll: this._buildPollLibrary(),
        checklist: this._buildChecklistLibrary(),
        reel: this._buildReelLibrary(),
        custom_question: this._buildQuestionLibrary(),
        spark: this._buildSparkLibrary(),
      }
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForOpen()
        this.refreshLibraries()
        this.bindFocusRefresh()
      } else {
        this.unbindFocusRefresh()
      }
    },
    initialProgram(next) {
      if (!this.value || !next || !this.program || next.id !== this.program.id) return
      this.program = JSON.parse(JSON.stringify(next))
      if (!this.program.items) this.program.items = []
      this.openProgramFingerprint = this.programFingerprint(this.program)
    },
  },
  beforeDestroy() {
    this.unbindFocusRefresh()
  },
  methods: {
    // Expose the shared label helper so templates render "Day N" with
    // the same +1 shift the picker / preview / participant timeline use.
    dayLabel,
    // Pull fresh library content each time the editor opens. The
    // question store caches aggressively (getQuestions early-returns
    // when questionsall is already populated), so without forceRefresh
    // a question authored after the page first loaded — e.g. a new
    // Program-only question — would never appear in the picker. The
    // University / Town Hall actions already rebuild in full, so a
    // plain dispatch keeps those fresh too.
    refreshLibraries() {
      this.libraryRefreshAt = Date.now()
      this.$store
        .dispatch('question/getQuestions', { forceRefresh: true })
        .catch(() => {})
      this.$store
        .dispatch('custom/getQuestionsCustom', { forceRefresh: true })
        .catch(() => {})
      this.$store
        .dispatch('happilyvillageUniversity/getUniversity')
        .catch(() => {})
      this.$store
        .dispatch('happilyvillageTownhall/getTownhall')
        .catch(() => {})
      this.$store
        .dispatch('sparks/getSparkCustom', this.$store.getters['authen/user'])
        .catch(() => {})
      this.refreshReels()
    },
    async refreshReels() {
      this.reelsLoading = true
      this.reelsLoadError = ''
      try {
        this.reelsAll = await loadProgramReels()
      } catch (_) {
        this.reelsLoadError = "Reels couldn't be loaded. Try again."
      } finally {
        this.reelsLoading = false
      }
    },
    // Refetch when the tab regains focus while the editor is open —
    // covers the deep-link flow where a question is authored in a
    // separate tab. Silent: the editor binds no loading state and the
    // picker's search query is local, so the list just updates in
    // place. Debounced so rapid tab-switching doesn't hammer Firebase,
    // and skipped right after the open-time refresh already ran.
    bindFocusRefresh() {
      if (this.libraryFocusHandler) return
      this.libraryFocusHandler = () => {
        if (document.visibilityState === 'hidden') return
        if (Date.now() - this.libraryRefreshAt < 3000) return
        this.refreshLibraries()
      }
      window.addEventListener('focus', this.libraryFocusHandler)
      document.addEventListener('visibilitychange', this.libraryFocusHandler)
    },
    unbindFocusRefresh() {
      if (!this.libraryFocusHandler) return
      window.removeEventListener('focus', this.libraryFocusHandler)
      document.removeEventListener('visibilitychange', this.libraryFocusHandler)
      this.libraryFocusHandler = null
    },
    typeMeta(type) {
      return TYPE_META[type] || TYPE_META.session
    },
    // Preview-rail badge only renders for rewards that are fully
    // specified — same gate the readiness hint uses.
    hasValidReward(item) {
      const r = item && item.completionReward
      if (!r) return false
      return !!r.currency && Number(r.amount) > 0
    },
    libraryFor(type) {
      return this.librariesByType[type] || []
    },
    // Library builders pull purely from real Vuex state. If a getter
    // hasn't populated yet the picker renders its "Nothing in …"
    // empty state with a Create-one link — no mock placeholder rows.
    // Program runs clone session / topic / poll / checklist into the same
    // collection as the library original, stamped with `cloneOf: 'program'`.
    // _townhallByType + _sessionsLibrarySource already exclude clones; the
    // builders below just map into picker-row shape.
    _buildSessionLibrary() {
      return this._buildLibraryFromSource(this._sessionsLibrarySource, (s) => {
        const title = this._firstText(s.title, s.class_name)
        const meta = this._joinText(s.meta, s.class_description)
        return {
          title,
          meta,
          search: this._searchText(title, meta, s.create_name, s.topic_name, s.class_type),
        }
      })
    },
    _buildTopicLibrary() {
      // CSS clamps the description in the picker preview; don't pre-slice
      // here or the Preview popover hides content.
      return this._buildLibraryFromSource(this._townhallByType.topic, (t) => {
        const title = this._firstText(t.title)
        return {
          title,
          meta: this._joinText(t.description, t.meta),
          search: this._searchText(title, t.description, t.meta, t.create_name, t.type),
        }
      })
    },
    _buildPollLibrary() {
      return this._buildLibraryFromSource(this._townhallByType.poll, (t) => {
        const title = this._firstText(t.title)
        return {
          title,
          meta: this._joinText(t.meta, t.description) || (Array.isArray(t.options) ? `${t.options.length} options` : ''),
          search: this._searchText(title, t.meta, t.description, t.create_name, t.type, t.options),
        }
      })
    },
    _buildChecklistLibrary() {
      return this._buildLibraryFromSource(this._townhallByType.checklist, (t) => {
        const title = this._firstText(t.title)
        return {
          title,
          meta: this._joinText(t.description) || (Array.isArray(t.options) ? `${t.options.length} items` : ''),
          search: this._searchText(title, t.description, t.create_name, t.type, t.options),
        }
      })
    },
    _buildReelLibrary() {
      return buildProgramReelLibrary(this.reelsAll)
    },
    // Only Program-only questions belong here. Legacy follow-up questions
    // (parent='Y' without the Program-only discriminator) are answer-branch
    // children, not program content; including them floods the picker.
    _buildQuestionLibrary() {
      const source = [...(this._questionsAll || []), ...(this._customQuestionsAll || [])]
        .filter((q) => this._isProgramOnlyQuestion(q))
      return this._buildLibraryFromSource(source, (q) => ({
        // English-first for display; `search` covers every language so an
        // English-speaking author isn't limited by the Thai-primary `question`.
        title: this._firstText(q.question_en, q.question, q.questions),
        meta: q.type_name || q.type || '',
        search: this._searchText(q.question_en, q.question, q.questions, q.type_name, q.type, q.category_name, q.power_skill),
        isProgramOnly: true,
      }))
    },
    _isProgramOnlyQuestion(q) {
      if (!q) return false
      return q.frequency === 'Program-only' || q.repeats === 'Program-only'
    },
    // Only Program-only Spark sets — those carrying the program_only
    // synthetic cohort. Scheduled sets live on the Micro Learning calendar.
    _buildSparkLibrary() {
      const source = (this._sparkSetsAll || []).filter((s) => this._isProgramOnlySparkSet(s))
      return this._buildLibraryFromSource(source, (s) => {
        const cards = Array.isArray(s.cardList) ? s.cardList : []
        return {
          title: this._firstText(s.name),
          meta: cards.length === 1 ? '1 card' : `${cards.length} cards`,
          search: this._searchText(s.name, cards.map((c) => c && (c.sparkTitle || c.name))),
          isProgramOnly: true,
        }
      })
    },
    _isProgramOnlySparkSet(s) {
      return !!s && Array.isArray(s.permissions) &&
        s.permissions.indexOf(PROGRAM_ONLY_KEY) !== -1
    },
    _isProgramOnlyLibraryItem(item) {
      if (!item) return false
      if (item.programOnly || item.program_only) return true
      const huddles = Array.isArray(item.huddles)
        ? item.huddles
        : (Array.isArray(item.huddle) ? item.huddle : [])
      return huddles.some((h) => {
        if (!h) return false
        if (Array.isArray(h)) return h[0] === PROGRAM_ONLY_KEY
        const value = h.key || h.value || h.id || ''
        return h.programOnly === true ||
          value === PROGRAM_ONLY_KEY ||
          String(value).toLowerCase() === 'program only'
      })
    },
    _prioritizeProgramOnly(items) {
      return (items || [])
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const scope = Number(!!b.item.isProgramOnly) - Number(!!a.item.isProgramOnly)
          return scope || a.index - b.index
        })
        .map(({ item }) => item)
    },
    _dedupeLibraryItems(items) {
      const seen = new Set()
      const out = []
      ;(items || []).forEach((item) => {
        const id = item && item.id
        if (!id || seen.has(id)) return
        seen.add(id)
        out.push(item)
      })
      return out
    },
    // Common shape for every library builder: map raw source docs through
    // `toRow` (which returns the per-row fields except id + isProgramOnly,
    // both of which are derived here), drop rows missing id/title, dedupe
    // by id, then prioritize Program-only rows. Dedupe before sort keeps
    // the sort cheaper and avoids drift between the four builders.
    _buildLibraryFromSource(source, toRow) {
      const rows = (source || []).map((src) => {
        const fields = toRow(src) || {}
        return {
          id: src.id || src.key,
          isProgramOnly: this._isProgramOnlyLibraryItem(src),
          ...fields,
        }
      }).filter((x) => x.id && x.title)
      return this._prioritizeProgramOnly(this._dedupeLibraryItems(rows))
    },
    // Per-batch clones live in the same collection as the library
    // originals. cloneOf is the canonical marker every clone-core stamps;
    // batchId+parentRef is a fallback for any legacy clone that predates it.
    _isProgramClone(item) {
      if (!item) return false
      return item.cloneOf === 'program' || Boolean(item.batchId && item.parentRef)
    },
    _firstText(...values) {
      const parts = this._flattenText(values)
      return parts.length ? parts[0] : ''
    },
    _joinText(...values) {
      return this._flattenText(values).join(' ')
    },
    _searchText(...values) {
      return this._joinText(...values).toLowerCase()
    },
    _flattenText(values) {
      const parts = []
      const visit = (value) => {
        if (value === null || value === undefined) return
        if (typeof value === 'string' || typeof value === 'number') {
          const s = String(value).trim()
          if (s) parts.push(s)
          return
        }
        if (Array.isArray(value)) {
          value.forEach(visit)
          return
        }
        if (typeof value === 'object') {
          Object.values(value).forEach(visit)
        }
      }
      values.forEach(visit)
      return parts
    },
    placeholderFor(item) {
      const found = this.libraryFor(item.type).find((x) => x.id === item.libraryId)
      return found ? found.title : ''
    },
    previewTitle(item) {
      const override = item.participantTitle && item.participantTitle.trim()
      if (override) return override
      const fromLibrary = item.libraryId && this.placeholderFor(item)
      if (fromLibrary) return fromLibrary
      return PICK_PLACEHOLDER[item.type] || 'Pick content'
    },
    // Seed for the reward reason field — surfaced to the recipient
    // when they receive the grant. Verb is per-type (reading a topic,
    // voting in a poll, completing a session / checklist, answering a
    // question), so the message matches the action that actually fired
    // the trigger. Author can override after.
    defaultRewardReason(item) {
      const { verb, noun } = COMPLETION_VERB[item.type] || COMPLETION_VERB.session
      const title = this.previewTitle(item)
      if (!title || title === PICK_PLACEHOLDER[item.type]) {
        return `For ${verb} this ${noun}`
      }
      return `For ${verb} "${title}"`
    },
    isCloneable(type) {
      // Topics, polls, sessions, and checklists have per-batch state
      // (comments, votes, signups, tick state) and are cloned by
      // default. Custom questions and sparks have per-user state
      // already and are always shared.
      return type === 'topic' || type === 'poll' || type === 'session' || type === 'checklist'
    },
    shareModeHint(item) {
      const shared = item.shareMode === 'shared'
      switch (item.type) {
        case 'topic':
          return shared
            ? 'All runs see and comment on the same topic. Best for evergreen content with rolling enrolment.'
            : 'Each run gets its own copy. Comments are isolated to that run.'
        case 'poll':
          return shared
            ? 'All runs contribute to the same vote tally.'
            : 'Each run has its own vote tally.'
        case 'session':
          return shared
            ? 'All runs join the same session at the library date.'
            : 'Each run gets its own session scheduled relative to its start date.'
        case 'checklist':
          return shared
            ? 'All runs tick the same checklist. Progress is aggregated across runs.'
            : 'Each run gets its own checklist. Tick state is isolated to that run.'
        default:
          return ''
      }
    },
    resetForOpen() {
      if (this.initialProgram) {
        this.program = JSON.parse(JSON.stringify(this.initialProgram))
        if (!this.program.items) this.program.items = []
      } else {
        this.program = blankProgram()
      }
      this.openProgramFingerprint = this.programFingerprint(this.program)
      this.saving = false
      this.saveError = ''
    },
    programFingerprint(program) {
      const items = ((program && program.items) || []).map((item) => ({
        type: item.type || '',
        offsetDays: Number(item.offsetDays) || 0,
        libraryId: item.libraryId || null,
        participantTitle: item.participantTitle || '',
        shareMode: item.shareMode || 'per_batch',
        completionReward: item.completionReward
          ? {
            currency: item.completionReward.currency || '',
            amount: Number(item.completionReward.amount) || 0,
            reason: item.completionReward.reason || '',
          }
          : null,
      }))
      return JSON.stringify({
        title: (program && program.title) || '',
        description: (program && program.description) || '',
        items,
      })
    },
    hasProgramChanges() {
      return this.programFingerprint(this.program) !== this.openProgramFingerprint
    },
    updateField(key, value) {
      this.program = { ...this.program, [key]: value }
    },
    updateItem(idx, key, value) {
      const next = this.items.slice()
      next[idx] = { ...next[idx], [key]: value }
      this.program = { ...this.program, items: next }
    },
    addItem(type) {
      const lastOffset = this.items.length > 0
        ? Math.max(...this.items.map((item) => Number(item.offsetDays) || 0))
        : -1
      const next = [
        ...this.items,
        {
          id: newItemId(),
          type,
          offsetDays: lastOffset + 1,
          libraryId: null,
          participantTitle: '',
          // Published reels are shared media: every run points at the same
          // video so playback, reactions, and analytics stay together.
          shareMode: type === 'reel' ? 'shared' : 'per_batch',
          completionReward: null,
        },
      ]
      this.program = { ...this.program, items: next }
    },
    removeItem(idx) {
      const next = this.items.filter((_, i) => i !== idx)
      this.program = { ...this.program, items: next }
    },
    moveItem(idx, dir) {
      const target = idx + dir
      if (target < 0 || target >= this.items.length) return
      const next = this.items.slice()
      const sourceDay = Number(next[idx].offsetDays) || 0
      const targetDay = Number(next[target].offsetDays) || 0
      next[idx] = { ...next[idx], offsetDays: targetDay }
      next[target] = { ...next[target], offsetDays: sourceDay }
      const [picked] = next.splice(idx, 1)
      next.splice(target, 0, picked)
      this.program = { ...this.program, items: next }
    },
    friendlySaveError() {
      return "This program wasn't saved. Your edits are still here — check your connection and try again."
    },
    async saveAndClose(done) {
      if (this.saving) return false
      this.saving = true
      this.saveError = ''
      try {
        const saved = await this.saveHandler(this.program)
        this.program = JSON.parse(JSON.stringify(saved || this.program))
        this.openProgramFingerprint = this.programFingerprint(this.program)
        if (done) done()
        else this.$emit('input', false)
        return true
      } catch (error) {
        this.saveError = this.friendlySaveError(error)
        return false
      } finally {
        this.saving = false
      }
    },
    onSave() {
      return this.saveAndClose()
    },
    installTemplateContent() {
      this.$emit('install-template-content', this.program)
    },
    // Topbar X click. Routes through the same dirty-check as ESC.
    closeRequest() {
      if (this.saving) return
      this.attemptClose()
    },
    // el-dialog's :before-close hook (ESC, since show-close is false and
    // close-on-click-modal is false). `done` is supplied by Element UI;
    // calling it lets the dialog finish closing, skipping it keeps it open.
    onBeforeClose(done) {
      if (this.saving) return
      this.attemptClose(done)
    },
    attemptClose(done) {
      const finishClose = () => {
        if (done) done()
        else this.$emit('input', false)
      }
      if (!this.hasProgramChanges()) {
        finishClose()
        return
      }
      this.$confirm(
        'Save your changes, or discard your edits?',
        'Save changes?',
        {
          confirmButtonText: 'Save changes',
          cancelButtonText: 'Discard',
          // Without this, ESC on the confirm dialog rejects as 'cancel'
          // (same as the Discard button) — losing the user's edits.
          distinguishCancelAndClose: true,
          type: 'warning',
        }
      )
        .then(() => this.saveAndClose(done))
        .catch((action) => {
          if (action === 'cancel') {
            // Discard button — close without saving.
            finishClose()
          }
          // 'close' (ESC / X on the confirm) = "keep editing"; do nothing.
        })
    },
  },
}
</script>

<style>
.program-editor-dialog {
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(108, 48, 204, 0.06), 0 24px 48px rgba(15, 16, 32, 0.18);
  overflow: hidden;
  max-width: calc(100vw - 32px);
}
.program-editor-dialog .el-dialog__header { display: none; }
.program-editor-dialog .el-dialog__body { padding: 0; }
</style>

<style scoped>
.programs-editor {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 8vh);
}

/* Topbar */
.ed-topbar {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 18px;
  padding: 16px 24px;
  border-bottom: 1px solid #eef0f5;
  flex-shrink: 0;
}
.ed-topbar-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.ed-topbar-right {
  display: flex;
  gap: 10px;
  align-items: center;
}
.ed-back-btn {
  appearance: none;
  background: transparent;
  border: 1px solid #e2e4ea;
  color: #6b7280;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}
.ed-back-btn:hover {
  border-color: rgba(108, 48, 204, 0.30);
  color: #1a1a1a;
  background: #f2ecfb;
}
.ed-title-block { min-width: 0; }
.ed-program-title {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.005em;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 460px;
}

.ed-body {
  display: flex;
  flex: 1;
  min-height: 320px;
  overflow: hidden;
}
.ed-save-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 24px;
  border-bottom: 1px solid #f3c9c9;
  background: #fff5f5;
  color: #a32626;
  font-size: 13px;
  line-height: 1.45;
}
.ed-save-error i { margin-top: 2px; }
.ed-main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 24px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
}

.ed-preview {
  width: 340px;
  flex-shrink: 0;
  background: #eef0f5;
  overflow-y: auto;
  padding: 24px 22px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ed-preview-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e2e4ea;
}
.ed-preview-sub {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.005em;
}
.ed-preview-empty {
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: #8a8a8a;
  font-style: italic;
  border: 1px dashed #e2e4ea;
  border-radius: 8px;
  background: #fff;
}
.ed-preview-timeline {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ed-preview-day {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 10px;
  align-items: stretch;
}
.ed-preview-day-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding-top: 2px;
}
.ed-preview-day-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  padding: 4px 8px;
  background: #f2ecfb;
  color: #6c30cc;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 999px;
  white-space: nowrap;
}
.ed-preview-day-line {
  flex: 1;
  width: 2px;
  background: #d8dbe3;
  border-radius: 2px;
  margin-bottom: -8px;
}
.ed-preview-day.is-last .ed-preview-day-line {
  background: transparent;
}
.ed-preview-day-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 14px;
}
.ed-preview-item {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  transition: border-color .15s ease;
}
.ed-preview-item.is-incomplete {
  background: #fff;
  border: 1px dashed #c7cbd6;
}
.ed-preview-item-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}
.ed-preview-item-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ed-preview-item-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.35;
  word-break: break-word;
}
.ed-preview-item-title.is-muted {
  color: #8a8a8a;
  font-weight: 600;
  font-style: italic;
}
.ed-preview-item-meta {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #8a8a8a;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.ed-preview-shared-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  background: #f2ecfb;
  color: #6c30cc;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 999px;
}
.ed-preview-needs-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  background: #fff;
  color: #8a8a8a;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px dashed #c7cbd6;
  border-radius: 999px;
}
/* Reward badge — amber soft tint, matches the existing poll palette so it
 * reads as "earnable" without competing with the brand-purple type pills. */
.ed-preview-reward-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  background: #fdf6dc;
  color: #c89500;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 999px;
}

@media (max-width: 1199px) {
  .ed-preview { display: none; }
}

@media (max-width: 720px) {
  .programs-editor { max-height: calc(100vh - 24px); }
  .ed-topbar {
    grid-template-columns: 1fr;
    padding: 14px 16px;
  }
  .ed-topbar-right {
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .ed-readiness-hint {
    width: 100%;
    white-space: normal;
    text-align: right;
  }
  .ed-main { padding: 18px 16px 24px; }
  .ed-card { padding: 16px; }
}

/* Readiness hint in topbar */
.ed-readiness-hint {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a8a8a;
  white-space: nowrap;
  margin-right: 4px;
}

/* Cards */
.ed-card {
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  padding: 20px 22px;
}
.ed-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 16px;
}
.ed-helper {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a8a;
}

/* Form */
.ed-form { display: flex; flex-direction: column; gap: 14px; }
.ed-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.ed-field { display: flex; flex-direction: column; gap: 6px; }

/* Sequence */
.ed-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ed-item {
  background: #fafbfd;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color .15s ease;
}
.ed-item.is-incomplete {
  border-color: rgba(247, 185, 36, 0.40);
  background: rgba(247, 185, 36, 0.04);
}
.ed-item-head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ed-item-day {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ed-item-day .qd-eyebrow { white-space: nowrap; }
.ed-item-day >>> .el-input-number {
  width: 64px;
}
.ed-item-day >>> .el-input-number .el-input__inner {
  text-align: center;
  padding: 0 6px;
  font-weight: 700;
  color: #1a1a1a;
}
.ed-item-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.ed-item-pill i { font-size: 12px; }
.ed-item-spacer { flex: 1; }
.ed-item-reorder {
  display: flex;
  gap: 4px;
}
.ed-item-icon-btn {
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  color: #8a8a8a;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  transition: background .15s ease, color .15s ease, border-color .15s ease;
}
.ed-item-icon-btn:hover:not(:disabled) {
  background: #f2ecfb;
  color: #6c30cc;
  border-color: rgba(108, 48, 204, 0.20);
}
.ed-item-icon-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.ed-item-icon-btn.is-danger:hover:not(:disabled) {
  background: rgba(204, 48, 48, 0.08);
  color: #cc3030;
  border-color: rgba(204, 48, 48, 0.20);
}
.ed-item-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ed-item-override {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Share-vs-clone toggle for cloneable items */
.ed-item-share {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  background: #fafbfd;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  margin-top: 4px;
}
.ed-item-share-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.ed-item-share-title {
  font-size: 12px;
  font-weight: 700;
  color: #1a1a1a;
}
.ed-item-share-sub {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.45;
}

/* "Add the next item" card — lives inline at the bottom of .ed-items so it
 * visually continues the sequence rhythm instead of looking like a separate
 * strip beneath the list. */
/* Copy sits above the type chips so both get the full card width — a
 * side-by-side layout squeezes the description into a sliver once there
 * are six-plus chips, forcing ugly mid-word wraps. */
.ed-add-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 16px;
  background: #ffffff;
  border: 1.5px dashed rgba(108, 48, 204, 0.25);
  border-radius: 10px;
  transition: border-color .15s ease, background .15s ease;
}
.ed-add-card:hover {
  border-color: rgba(108, 48, 204, 0.40);
  background: linear-gradient(135deg, rgba(108, 48, 204, 0.025) 0%, #ffffff 100%);
}
.ed-add-card.is-first {
  padding: 22px 20px;
  gap: 14px;
  text-align: center;
}
.ed-add-card__copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.ed-add-card__title {
  font-size: 13px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.005em;
}
.ed-add-card__sub {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.45;
}
/* A responsive grid of type tiles — auto-fill keeps rows balanced
 * (2 columns in the editor's left pane, more if there's room) and
 * grid stretch gives every tile in a row equal height even when one
 * blurb wraps to a second line. */
.ed-add-card__btns {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(208px, 1fr));
  gap: 8px;
}

/* Each tile is the entry point to a program item, so it teaches as
 * well as triggers: a colored icon swatch carrying the type's own
 * brand tint at rest (--add-tint / --add-tone, set inline per button),
 * a label, and a one-line blurb so "Session" or "Spark set" isn't
 * left to guesswork. */
.ed-add-btn {
  appearance: none;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.ed-add-btn:hover {
  /* Hovering Session glows purple, Poll glows amber, etc. — falls back
   * to neutral purple when CSS vars aren't supplied. */
  border-color: var(--add-tone, #6c30cc);
  box-shadow: 0 2px 10px rgba(26, 26, 26, 0.08);
}
.ed-add-btn__icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  background: var(--add-tint, #f2ecfb);
  color: var(--add-tone, #6c30cc);
  transition: background .15s ease, color .15s ease;
}
.ed-add-btn:hover .ed-add-btn__icon {
  background: var(--add-tone, #6c30cc);
  color: #ffffff;
}
.ed-add-btn__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.ed-add-btn__label {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  transition: color .15s ease;
}
.ed-add-btn:hover .ed-add-btn__label {
  color: var(--add-tone, #6c30cc);
}
.ed-add-btn__blurb {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.3;
}
</style>
